import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { SiteStats } from '@2x3/shared';
import { api } from '../lib/api.js';
import { plural } from '../lib/format.js';
import { useSession } from '../lib/session.js';

export default function Home() {
  const { me } = useSession();
  const navigate = useNavigate();
  const [stats, setStats] = useState<SiteStats | null>(null);
  const [nextBusy, setNextBusy] = useState(false);
  const [nextNone, setNextNone] = useState(false);

  useEffect(() => {
    api.stats().then(setStats, () => setStats(null));
  }, []);

  const start = async (): Promise<void> => {
    setNextBusy(true);
    try {
      const { id } = await api.next();
      if (id) navigate(`/clip/${id}`);
      else setNextNone(true);
    } finally {
      setNextBusy(false);
    }
  };

  return (
    <div className="home">
      <section className="hero">
        <h1>
          Copy real CW.
          <br />
          <span className="accent">Become the answer key.</span>
        </h1>
        <p className="lead">
          These clips are real contacts, recorded off the air on 40 and 20 meters. Copy one by ear and your copy
          becomes part of the answer key a new multi-signal CW skimmer is scored against.
        </p>

        {me ? (
          <div className="cta">
            <button type="button" className="btn-primary btn-big" onClick={() => void start()} disabled={nextBusy}>
              {nextBusy ? 'Finding a clip...' : 'Start copying'}
            </button>
            <span className="muted">
              Signed in as <strong className="call">{me.callsign}</strong> &middot; {plural(me.copies, 'copy', 'copies')}{' '}
              done &middot; <Link to="/clips">{me.toCopy} to go</Link>
            </span>
            {nextNone && <p className="alert">You've copied every open clip. Thank you! New batches go up as they're carved.</p>}
          </div>
        ) : (
          <div className="cta">
            <Link to="/clips" className="btn-primary btn-big">
              Listen to the clips
            </Link>
            <span className="muted">
              Copying is by invitation from WB5SKM. If you have an invite link, open it on this device.
            </span>
          </div>
        )}
      </section>

      <section className="steps">
        <div className="step">
          <span className="step-n">1</span>
          <h3>Listen</h3>
          <p>
            Each clip is one frequency from a recording of the whole band: a QSO, a CQ, a pileup, a ragchew. Slow it down
            without changing the pitch, and loop the hard stretch.
          </p>
        </div>
        <div className="step">
          <span className="step-n">2</span>
          <h3>Copy what was sent</h3>
          <p>
            Every character and every repeat, exactly as keyed. Mark a doubtful letter <code>W7[K]Z</code> and a lost
            stretch <code>#</code>. <Link to="/guide">The conventions</Link> take two minutes to read.
          </p>
        </div>
        <div className="step">
          <span className="step-n">3</span>
          <h3>Save it</h3>
          <p>
            Your copy goes to WB5SKM and no further. You never see another copier's work, and no copier sees yours.
            That independence is what makes two copies that agree worth trusting.
          </p>
        </div>
      </section>

      <section className="why">
        <h2>Why ears?</h2>
        <p>
          A decoder can only be measured against what was actually sent. For a real QSO, the only honest witness is an
          operator who copied it. Scoring against another decoder measures how often the two agree, not whether either
          is right. So the decoders are graded against you.
        </p>
      </section>

      {stats && (
        <section className="stats">
          <div>
            <span className="stat">{stats.clipsOpen}</span>
            <span className="muted">clips waiting</span>
          </div>
          <div>
            <span className="stat">{stats.copies}</span>
            <span className="muted">copies made</span>
          </div>
          <div>
            <span className="stat">{stats.copiers}</span>
            <span className="muted">{stats.copiers === 1 ? 'copier' : 'copiers'}</span>
          </div>
        </section>
      )}

      {stats && stats.contributors.length > 0 && (
        <section className="contributors">
          <h2>Copied by</h2>
          <p className="contributor-list">
            {stats.contributors.map((c) => (
              <span key={c.callsign} className="call" title={plural(c.copies, 'copy', 'copies')}>
                {c.callsign}
              </span>
            ))}
          </p>
        </section>
      )}
    </div>
  );
}
